import React from "react";
import { Box, Chip, Stack, Typography, useTheme } from "@mui/material";

const WIDTH = 1200;
const HEIGHT = 500;
const PAD = { top: 34, right: 28, bottom: 52, left: 82 };

function compactMoney(value, currency) {
  const abs = Math.abs(Number(value || 0));
  const sign = Number(value || 0) < 0 ? "-" : "";
  const prefix = currency === "USD" ? "US$" : currency === "ARS" ? "$" : `${currency} `;
  if (abs >= 1_000_000_000) return `${sign}${prefix}${(abs / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${sign}${prefix}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${prefix}${(abs / 1_000).toFixed(0)}k`;
  return `${sign}${prefix}${Math.round(abs).toLocaleString("es-AR")}`;
}

function fullMoney(value, currency) {
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: currency === "ARS" ? 0 : 2 }).format(Number(value || 0));
  } catch {
    return `${currency} ${Number(value || 0).toLocaleString("es-AR")}`;
  }
}

function labelPeriod(period, bucket) {
  const date = new Date(period);
  if (bucket === "month") return date.toLocaleDateString("es-AR", { month: "short", year: "2-digit", timeZone: "America/Argentina/Mendoza" });
  return date.toLocaleDateString("es-AR", { day: "2-digit", month: "short", timeZone: "America/Argentina/Mendoza" });
}

export default function OwnerFinanceHistoryChart({ series = [], metric = "cumulative", currency = "ARS", bucket = "month" }) {
  const theme = useTheme();
  const [hoverIndex, setHoverIndex] = React.useState(null);
  const points = React.useMemo(() => {
    let cumulative = 0;
    return series.map((item) => {
      cumulative += Number(item.net || 0);
      return { ...item, cumulative };
    });
  }, [series]);

  const metricConfig = {
    cumulative: { key: "cumulative", label: "Resultado acumulado", color: theme.palette.primary.main },
    net: { key: "net", label: "Resultado por período", color: theme.palette.primary.main },
    income: { key: "income", label: "Ingresos", color: theme.palette.success.main },
    expense: { key: "expense", label: "Gastos", color: theme.palette.error.main },
  }[metric];

  if (!points.length) {
    return (
      <Box sx={{ minHeight: 500, display: "grid", placeItems: "center", borderRadius: 3, bgcolor: "action.hover" }}>
        <Box sx={{ textAlign: "center", px: 3 }}>
          <Typography sx={{ fontWeight: 950, fontSize: 19 }}>Todavía no hay puntos para este filtro</Typography>
          <Typography color="text.secondary" sx={{ mt: .5, fontWeight: 650 }}>Probá ampliar el período o cambiar cuenta/moneda.</Typography>
        </Box>
      </Box>
    );
  }

  const values = points.map((p) => Number(p[metricConfig.key] || 0));
  const rawMin = Math.min(...values, 0);
  const rawMax = Math.max(...values, 0);
  const span = Math.max(rawMax - rawMin, 1);
  const min = rawMin - span * 0.08;
  const max = rawMax + span * 0.08;
  const chartW = WIDTH - PAD.left - PAD.right;
  const chartH = HEIGHT - PAD.top - PAD.bottom;
  const xAt = (index) => PAD.left + (points.length <= 1 ? chartW / 2 : (index / (points.length - 1)) * chartW);
  const yAt = (value) => PAD.top + ((max - value) / (max - min || 1)) * chartH;
  const zeroY = yAt(0);
  const linePath = points.map((p, i) => `${i ? "L" : "M"}${xAt(i).toFixed(2)},${yAt(Number(p[metricConfig.key] || 0)).toFixed(2)}`).join(" ");
  const areaPath = `${linePath} L${xAt(points.length - 1)},${zeroY} L${xAt(0)},${zeroY} Z`;
  const gradientId = `owner-finance-${metric}`;
  const gridValues = Array.from({ length: 5 }, (_, i) => max - ((max - min) * i) / 4);
  const labelEvery = Math.max(1, Math.ceil(points.length / 8));
  const hovered = hoverIndex == null ? null : points[hoverIndex];

  return (
    <Box sx={{ position: "relative", width: "100%", minHeight: 500 }}>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} width="100%" height="500" preserveAspectRatio="xMidYMid meet" role="img" aria-label={`Gráfico de ${metricConfig.label}`}>
        <defs>
          <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={metricConfig.color} stopOpacity="0.26" />
            <stop offset="100%" stopColor={metricConfig.color} stopOpacity="0.015" />
          </linearGradient>
        </defs>
        {gridValues.map((value, index) => {
          const y = yAt(value);
          return (
            <g key={index}>
              <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y} y2={y} stroke={theme.palette.divider} strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <text x={PAD.left - 12} y={y + 4} textAnchor="end" fill={theme.palette.text.secondary} fontSize="13" fontWeight="700">{compactMoney(value, currency)}</text>
            </g>
          );
        })}
        <line x1={PAD.left} x2={WIDTH - PAD.right} y1={zeroY} y2={zeroY} stroke={theme.palette.text.secondary} strokeOpacity=".38" strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
        <path d={areaPath} fill={`url(#${gradientId})`} />
        <path d={linePath} fill="none" stroke={metricConfig.color} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        {points.map((point, i) => {
          const x = xAt(i);
          const y = yAt(Number(point[metricConfig.key] || 0));
          const active = i === hoverIndex;
          return (
            <g key={`${point.period}-${i}`}>
              {(i % labelEvery === 0 || i === points.length - 1) ? (
                <text x={x} y={HEIGHT - 17} textAnchor="middle" fill={theme.palette.text.secondary} fontSize="13" fontWeight="700">{labelPeriod(point.period, bucket)}</text>
              ) : null}
              <rect x={Math.max(PAD.left, x - chartW / Math.max(points.length, 1) / 2)} y={PAD.top} width={Math.max(16, chartW / Math.max(points.length, 1))} height={chartH} fill="transparent" onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)} />
              {active ? <line x1={x} x2={x} y1={PAD.top} y2={PAD.top + chartH} stroke={metricConfig.color} strokeOpacity=".42" strokeDasharray="5 5" vectorEffect="non-scaling-stroke" /> : null}
              {active ? <circle cx={x} cy={y} r="7" fill={theme.palette.background.paper} stroke={metricConfig.color} strokeWidth="4" vectorEffect="non-scaling-stroke" /> : null}
            </g>
          );
        })}
      </svg>

      {hovered ? (
        <Box sx={{ position: "absolute", top: 18, right: 18, minWidth: 220, p: 1.4, borderRadius: 2.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider", boxShadow: 4, pointerEvents: "none" }}>
          <Typography sx={{ fontWeight: 950 }}>{labelPeriod(hovered.period, bucket)}</Typography>
          <Typography sx={{ mt: .35, fontSize: 21, fontWeight: 1000, color: metricConfig.color }}>{fullMoney(hovered[metricConfig.key], currency)}</Typography>
          <Stack direction="row" spacing={.6} sx={{ mt: .8 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`Ingresos ${fullMoney(hovered.income, currency)}`} />
            <Chip size="small" label={`Gastos ${fullMoney(hovered.expense, currency)}`} />
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
}
